import moment from "jalali-moment";
import {
  LegacyRef,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactPaginate from "react-paginate";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import endpoints from "../../config/endpoints";
import usePagination from "../../hooks/usePagination";
import { useResponsive } from "../../hooks/useResponsive";
import { popupSlice } from "../../services/reducers";
import { setLoading } from "../../services/reducers/homeSlice";
import restApi from "../../services/restApi";
import tools from "../../utils/tools";
import { Sidebar } from "../../layouts/Sidebar";
import Bill from "./Bill";
import FeedbackModal from "./FeedbackModal";
import Message from "../../layouts/Modal/Message";

const Orders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<any>([]);
  const [query, setQuery] = useState(searchParams.get("query") || "");
  const [tab, setTab] = useState(searchParams.get("tab") || "all");
  const {
    pageCount,
    endOffset,
    itemOffset,
    itemsPerPage,
    setPageCount,
    setItemOffset,
    setItemsPerPage,
  } = usePagination(data);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsive();
  const tabTitles = {
    all: "همه",
    AwaitingPayment: "در انتظار پرداخت",
    Paid: "پرداخت شده",
    Assigned: "محول شده",
    InProgress: "در حال انجام",
    Canceled: "لغو شده",
    Done: "تمام شده",
  };

  const deleteOrder = async (id: number) => {
    const result = await Swal.fire({
      title: "آیا مطمئن هستید؟",
      text: "این عمل قابل بازگشت نیست",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#c89dc7",
      confirmButtonText: "بله، حذف کن",
      cancelButtonText: "انصراف",
    });

    if (result.isConfirmed) {
      dispatch(setLoading(true));

      try {
        const res = await restApi(
          process.env.REACT_APP_BASE_URL + "/admin/order/" + id,
          true,
        ).delete({});

        if (res.code == 204) {
          Swal.fire({
            title: "موفق",
            text: "سفارش با موفقیت حذف شد",
            icon: "success",
            confirmButtonText: "متوجه شدم",
          });
          fetchOrders(); // Refresh data
        } else {
          Swal.fire({
            title: "ناموفق",
            text: res?.data || "خطا در حذف سفارش",
            icon: "error",
            confirmButtonText: "متوجه شدم",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "خطا",
          text: "خطا در اتصال به سرور",
          icon: "error",
          confirmButtonText: "متوجه شدم",
        });
      }

      dispatch(setLoading(false));
    }
  };

  const list = () => {
    const rows: ReactElement[] = [];

    data?.orders?.map((order: any, index: number) => {
      rows.push(
        <tr className="dashTr2" key={"order" + index}>
          <td className="">
            <div className="svgContainer">
              <i
                className={`trash clickable iconSize`}
                onClick={() => deleteOrder(order.id)}
                title="حذف"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && deleteOrder(order.id)}
              ></i>
              <i
                className="edit clickable iconSize"
                onClick={() => navigate("/order/edit/" + order.id)}
                title="ویرایش"
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && navigate("/order/edit/" + order.id)
                }
              ></i>
              <i
                className="copyIcon clickable iconSize"
                onClick={() => navigate("/order/add?copyOf=" + order.id)}
                title="کپی"
                role="button"
              ></i>
              <i
                className="usersSvg clickable iconSize"
                onClick={() =>
                  dispatch(popupSlice.middle(<Bill order={order} />))
                }
                title="محول کردن"
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  dispatch(popupSlice.middle(<Bill order={order} />))
                }
              ></i>
              <i
                className="messageIcon clickable iconSize"
                onClick={() =>
                  dispatch(popupSlice.middle(<Message user={order.user} />))
                }
                title="پیام"
                role="button"
              ></i>
              {order.isFeedbacked && (
                <i
                  className="feedbackIcon clickable"
                  onClick={() =>
                    dispatch(popupSlice.middle(<FeedbackModal order={order} />))
                  }
                  title="بازخورد"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    dispatch(popupSlice.middle(<FeedbackModal order={order} />))
                  }
                ></i>
              )}
            </div>
          </td>
          <td>{tools.formatPrice(order.finalPrice)}</td>
          <td>
            {order.worker
              ? `${order.worker?.name} ${order.worker?.lastName}`
              : "-"}
          </td>
          <td>{order.service?.title}</td>
          <td>{order.status}</td>
          <td>{order?.user?.name + " " + order?.user?.lastName}</td>
          <td>
            <div className="column">
              <p>{order?.date + " - " + order?.fromTime}</p>
              <p>{moment(order?.createdAt).format("jYYYY/jMM/jDD HH:mm:ss")}</p>
            </div>
          </td>
          <td>{order?.code}</td>
          <td>{index + 1}</td>
        </tr>,
      );
    });

    return rows;
  };

  const fetchOrders = async () => {
    !query && dispatch(setLoading(true));

    const params: any = {
      page: searchParams.get("page") || 1,
      perPage: itemsPerPage,
      query: query,
    };

    if (tab != "all") {
      params.status = tab;
    }

    try {
      const res = await restApi(endpoints.order.index, true).get(params);
      setData({
        ...res.data,
        statusCount: {
          ...res.data.statusCount,
          all: {
            title: "همه",
            count: Object.values(res.data.statusCount).reduce(
              (acc, curr) => acc + ((curr as any).count || 0),
              0,
            ),
          },
        },
      });
    } catch (error) {
      console.error("Error fetching orders:", error);
      Swal.fire({
        title: "خطا",
        text: "خطا در دریافت اطلاعات",
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    } finally {
      !query && dispatch(setLoading(false));
    }
  };
  useEffect(() => {
    fetchOrders();
  }, [searchParams.get("page"), itemsPerPage, query, tab]);

  useEffect(() => {
    if (data.count) {
      const newPage =
        Number(searchParams.get("page")) <= Math.ceil(data.count / itemsPerPage)
          ? searchParams.get("page") || "1"
          : "1";
      setSearchParams({
        ["page"]: newPage,
        ["tab"]: tab,
        query: query,
      });
    }
  }, [data]);

  return (
    <>
      <div className="dashboardBody">
        <Sidebar />

        <main className="dashBoardMain">
          <h1 className="dashBoardTitle">سفارش ها</h1>

          <div className="dashTabs">
            {Object.entries(tabTitles).map(([key, value]) => (
              <span
                key={key}
                className={`ordersTag clickable ${key == tab ? "activeTab" : ""}`}
                onClick={() => {
                  setTab(key);
                  setSearchParams({ ["page"]: "1", ["tab"]: key });
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setTab(key);
                    setSearchParams({ ["page"]: "1", ["tab"]: key });
                  }
                }}
              >
                {value}
                {data?.statusCount && (
                  <span
                    className={`numberTag ${key == tab ? "activeTab" : ""}`}
                  >
                    {data?.statusCount[key]?.count || 0}
                  </span>
                )}
              </span>
            ))}
          </div>

          <div className="searchContainer">
            <span className="backSpan keepRight">
              <span
                className="dashboardHeader clickable"
                onClick={() => navigate("/order/add")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && navigate("/order/add")}
              >
                <p>ثبت سفارش جدید</p>
                <i className="plusIcon"></i>
              </span>
            </span>

            <div className="itemsPerPage">
              <label htmlFor="itemPerPage">تعداد در صفحه</label>
              <input
                id="itemPerPage"
                type="number"
                min="1"
                max="100"
                defaultValue={itemsPerPage}
                onChange={(input) => {
                  const value = Number(input.target.value);
                  if (value && value > 0 && value <= 100) {
                    setItemsPerPage(value);
                  }
                }}
                onBlur={(e) => {
                  if (!e.target.value || Number(e.target.value) <= 0) {
                    e.target.value = itemsPerPage.toString();
                  }
                }}
              />
            </div>
            <div className="dashboardseaechBox">
              <i className="dashMagnifierIcon"></i>
              <input
                className="dashSearchInput"
                placeholder="جستجو"
                value={query}
                onChange={(input) => setQuery(input.target.value)}
                aria-label="جستجوی سفارشات"
              />
            </div>
          </div>
          <ReactPaginate
            breakLabel="..."
            nextLabel="بعدی >"
            onPageChange={(event) => {
              setSearchParams({
                ["page"]: (Number(event.selected) + 1).toString(),
                ["tab"]: tab,
              });
              setItemOffset(
                (event.selected * itemsPerPage) % (data?.orders?.length || 1),
              );
            }}
            forcePage={Number(searchParams.get("page")) - 1}
            initialPage={
              searchParams.get("page") != null
                ? Number(searchParams.get("page")) - 1
                : 0
            }
            pageRangeDisplayed={isMobile ? 3 : 5}
            pageCount={pageCount}
            previousLabel="< قبلی"
            renderOnZeroPageCount={null}
            className="pagination"
            pageClassName="paginationBreak"
            previousClassName="paginationBreak"
            nextClassName="paginationBreak"
            activeClassName="paginationActive"
            disabledClassName="paginationDisabled"
          />
          <div className="table-container">
            <table>
              <thead>
                <tr className="dashTr1 blueText">
                  <th>عملیات</th>
                  <th>مبلغ کل</th>
                  <th>استایلیست</th>
                  <th>خدمت</th>
                  <th>وضعیت سفارش</th>
                  <th>کاربر</th>
                  <th>تاریخ</th>
                  <th>شماره سفارش</th>
                  <th>ردیف</th>
                </tr>
              </thead>
              <tbody>{list()}</tbody>
            </table>
          </div>

          <ReactPaginate
            breakLabel="..."
            nextLabel="بعدی >"
            onPageChange={(event) => {
              setSearchParams({
                ["page"]: (Number(event.selected) + 1).toString(),
                ["tab"]: tab,
              });
              setItemOffset(
                (event.selected * itemsPerPage) % (data?.orders?.length || 1),
              );
            }}
            forcePage={Number(searchParams.get("page")) - 1}
            initialPage={
              searchParams.get("page") != null
                ? Number(searchParams.get("page")) - 1
                : 0
            }
            pageRangeDisplayed={isMobile ? 3 : 5}
            pageCount={pageCount}
            previousLabel="< قبلی"
            renderOnZeroPageCount={null}
            className="pagination"
            pageClassName="paginationBreak"
            previousClassName="paginationBreak"
            nextClassName="paginationBreak"
            activeClassName="paginationActive"
            disabledClassName="paginationDisabled"
          />
        </main>
      </div>
    </>
  );
};

export default Orders;
